import {useEffect, useRef} from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import busImg from '../../asset/images/Bus.png'

function BusMap({center, schoolLocation, busGeoJson, lookAtBus, setLookAtBus}) {
  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const busMarkersRef = useRef({}) // Store bus markers by bus number

  /**
   * Initializes the Mapbox map.
   */
  const initializeMap = () => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      center: [center.lng, center.lat],
      zoom: 16.5,
      maxZoom: 19,
      pitch: 60,
      attributionControl: false,
    })

    return map
  }
  const addMapEvents = (map) => {
    map.on('dragstart', () => {
      setLookAtBus(false)
    })
  }
  /**
   * Adds a school marker to the map.
   * @param {Object} map - The Mapbox map instance.
   */
  const addSchoolMarker = (map) => {
    const schoolEl = document.createElement('i')
    schoolEl.classList.add('las', 'la-city', 'fs-2hx', 'text-warning', 'bg-white', 'p-2', 'rounded')

    new mapboxgl.Marker(schoolEl).setLngLat([schoolLocation.lng, schoolLocation.lat]).addTo(map)
  }

  /**
   * Adds bus markers to the map.
   * @param {Object} map - The Mapbox map instance.
   */
  const addBusMarkers = (map) => {
    busGeoJson.features.forEach((feature) => {
      const busEl = document.createElement('img')
      busEl.src = busImg
      busEl.style.width = '60px'

      const busMarker = new mapboxgl.Marker(busEl)
        .setLngLat(feature.geometry.coordinates)
        .addTo(map)

      busMarkersRef.current[feature.properties.bus_number] = busMarker
    })
  }

  /**
   * Smoothly animates a marker to a new position.
   * @param {Object} marker - The Mapbox marker instance.
   * @param {Array} start - The starting coordinates [lng, lat].
   * @param {Array} end - The target coordinates [lng, lat].
   * @param {number} duration - Animation duration in milliseconds.
   */
  const animateMarker = (marker, start, end, duration) => {
    const startTime = performance.now()

    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime
      const progress = Math.min(elapsedTime / duration, 1)

      const lng = start[0] + (end[0] - start[0]) * progress
      const lat = start[1] + (end[1] - start[1]) * progress

      marker.setLngLat([lng, lat])

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }

  /**
   * Updates the positions of bus markers based on new geoJson data.
   */
  const updateMarkers = () => {
    busGeoJson.features.forEach((feature) => {
      const {bus_number} = feature.properties
      const marker = busMarkersRef.current[bus_number]

      if (marker) {
        const currentCoords = marker.getLngLat()
        const newCoords = feature.geometry.coordinates
        animateMarker(marker, [currentCoords.lng, currentCoords.lat], newCoords, 2000)
      }
    })
  }

  useEffect(() => {
    mapRef.current = initializeMap()

    addSchoolMarker(mapRef.current)
    addBusMarkers(mapRef.current)
    addMapEvents(mapRef.current)

    return () => {
      mapRef.current?.remove()
    }
  }, [])

  useEffect(() => {
    if (lookAtBus) mapRef.current?.easeTo({center, speed: 0.1})
  }, [center])

  useEffect(() => {
    updateMarkers()
  }, [busGeoJson])

  return <div id="map-container" ref={mapContainerRef} className="w-100 h-100" />
}

export default BusMap
